import {useQuery} from "react-query";
import {ReservationGroupService} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {Card, Stack, Typography} from "@mui/material";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import React from "react";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {GeneralSet} from "crqlar-react-common/dist/utils/generalSet";
import {useTranslation} from "react-i18next";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import {useProfiles} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {findProfileById, formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {InternalNode} from "crqlar-react-common/dist/models/profile/internalNode.model";

interface Props {
    id?: string
    reservationId?: string
    onOpenReservation?: (reservation: Reservation) => void
}

const ReservationGroupSimplePreview = (props: Props) => {

    const {t} = useTranslation()

    const reservationGroupWithReservationsQuery = useQuery(['reservationGroupWithReservations', props.id], async () => {
        if (!props.id) {
            return {}
        }

        const reservationGroup = await ReservationGroupService.getReservationGroupById(props.id)

        const reservationIds = reservationGroup.reservationIds || []
        const reservations = await ReservationService.getAllReservations({
            id: reservationIds,
            limit: {
                offset: 0,
                limit: 1000
            }
        })

        return {
            reservationGroup,
            reservations: reservations.content
        }
    })

    const {reservationGroup, reservations} = reservationGroupWithReservationsQuery.data || {}

    const resourceIds = new GeneralSet<string>()
    const profileIds = new GeneralSet<string>()
    reservations?.forEach(r => {
        resourceIds.addMulti(r.resourceIds || [])
        profileIds.addMulti(r.profileIds || [])
    })
    const resources = useResources(resourceIds.values())
    const profiles = useProfiles(profileIds.values())

    const renderSimpleReservation = (r: Reservation) => {
        const canOpen = r.id && r.id !== props.reservationId
        return (
            <Card
                key={r.id}
                sx={{
                    p: 1,
                    cursor: canOpen ? 'pointer' : 'default',
                    backgroundColor: r.id && r.id === props.reservationId ? 'primary.light' : 'inherit'
                }}
                onClick={() => canOpen && props.onOpenReservation?.(r)}
            >
                <Stack
                    direction={'row'}
                    gap={2}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                >
                    <Typography data-testid={'simple-reservation-date'}>
                        {formatDateDMYHM(r.expectedStartDate)}
                    </Typography>
                    <Typography data-testid={'simple-reservation-resource'}>
                        {
                            r.resourceIds?.map(resourceId => resources.find(r => r.id === resourceId)?.name).join(', ')
                        }
                    </Typography>
                    <Typography data-testid={'simple-reservation-status'}>
                        {t(`Reservation status ${r.status}`)}
                    </Typography>
                </Stack>
            </Card>
        )
    }

    const renderSimpleNote = (note: InternalNode) => {
        return (
            <Card
                sx={{
                    p: 1,
                    backgroundColor: 'inherit'
                }}
            >
                <Stack
                    gap={1}
                    direction={'row'}
                    justifyContent={'space-between'}
                >
                    <Typography>
                        {note.note}
                    </Typography>

                    <Stack gap={1}>
                        {
                            note.name &&
                            <Typography variant={'subtitle1'}>
                                {t('Context')}: ({note.name})
                            </Typography>
                        }

                        {
                            note.profileId &&
                            <Typography variant={'subtitle1'}>
                                {t('Guest')}: {formatProfileListName(findProfileById(profiles, note.profileId))}
                            </Typography>
                        }
                    </Stack>

                </Stack>
            </Card>
        )
    }

    return (
        <Stack gap={2}>
            <Stack gap={1}>
                <Typography>
                    {t('Reservations')}
                </Typography>
                {reservations?.map(renderSimpleReservation)}
            </Stack>

            <Stack gap={1}>
                <Typography>
                    {t('Reservation group notes')}
                </Typography>
                {reservationGroup?.notes?.map(renderSimpleNote)}
            </Stack>

            <ExternalInfoPreview
                externalInfo={reservationGroup?.externalInfo}
                additionalExternalInfo={reservationGroup?.additionalExternalInfo}
            />
        </Stack>

    )
}

export default ReservationGroupSimplePreview;
